import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import * as amplitude from '@amplitude/analytics-browser';

import useNavigateWithParams from 'libs/design-system/src/hooks/useNavigateWithParams';

import type { IButton } from 'libs/design-system/src/types/Button/button';
import type { ITransfersTableLogicProps } from '@app/dashboard/types/innerLayout/balance_and_transfers';

import { useLangStore } from '@app/dashboard/store/lang';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { COLUMNS_MAP } from '@app/dashboard/constants/balance_and_transfers';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
  BALANCE_TRANSFERS_EVENTS_VALUES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import { getCurrentPath } from '@app/dashboard/utils/getCurrentPath';

import { useTransferActions } from './useTransferActions';
import { getTargetAccount } from '../utils';
import {
  getSharedEventProperties,
  handleStoreLastAction,
} from '../utils/events-tracking';

export const useTransfersTableLogic = ({
  activeBalance,
}: ITransfersTableLogicProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [drawerState, setDrawerState] = useState<{
    open: boolean;
    transferId: string | null;
  }>({
    open: false,
    transferId: null,
  });
  const [transfersCount, setTransfersCount] = useState(0);

  const downloadAchRowId = useRef('');
  const firstMount = useRef(true);

  const { lang } = useLangStore();
  const { downloadAch } = useTransferActions();
  const navigateWithParams = useNavigateWithParams();

  const { accountId, haveAccounts } = getTargetAccount(
    activeBalance,
    searchParams
  );

  const hasManuallyChangedUrl =
    !accountId &&
    haveAccounts === false &&
    !!activeBalance?.isUrlManuallyChanged;

  const notHaveAccounts = haveAccounts === undefined && !accountId;

  const shouldDisableQuery = hasManuallyChangedUrl || notHaveAccounts;

  const transfersQueryKey = useMemo(
    () => [QUERY_KEYS.GET_TRANSFERS, lang, accountId ?? null],
    [lang, accountId]
  );

  const checkIfExistsOnTransfersPage = () => {
    return location.pathname === INNER_ROUTES.TRANSFERS;
  };

  const getCommonEventProperties = (row: Record<string, any>) => ({
    [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.AMOUNT]:
      row.net_amount ?? row.amount,
    ...getSharedEventProperties({
      transferId: row.transfer_id,
      status: row.status.key,
      currency: row.currency,
    }),
    [SHARED_EVENTS_PARAMS.PAGE]: getCurrentPath(),
  });

  const handleTableRowClick = (row: Record<string, any>) => {
    if (!row.transfer_id) return;

    const eventName = checkIfExistsOnTransfersPage()
      ? BALANCE_TRANSFERS_EVENTS.TRANSFERS_PAGE.TRANSFER_DETAIL_OPENED
      : BALANCE_TRANSFERS_EVENTS.BALANCE_PAGE.TRANSFER_DETAIL_OPENED;

    amplitude.track(eventName, {
      ...getCommonEventProperties(row),
    });

    handleStoreLastAction(eventName);

    navigateWithParams(
      INNER_ROUTES.SINGLE_TRANSFER.replace(':id', row.transfer_id)
    );
  };

  const handleOpenDrawer = (details: any) => {
    if (!details?.transfer_id) return;

    const entryPoint = checkIfExistsOnTransfersPage()
      ? BALANCE_TRANSFERS_EVENTS_VALUES.TRANSFERS_TABLE
      : BALANCE_TRANSFERS_EVENTS_VALUES.BALANCE_RECENT_TRANSFERS;
    const eventName =
      BALANCE_TRANSFERS_EVENTS.TRANSFERS_PAGE.TRANSFER_QUICK_VIEW_OPENED;

    amplitude.track(eventName, {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.ENTRY_POINT]: entryPoint,
      ...getCommonEventProperties(details),
    });

    handleStoreLastAction(eventName);

    setDrawerState({
      open: true,
      transferId: details?.transfer_id,
    });
  };

  const handleCloseDrawer = () => {
    if (!drawerState.open) return;

    setDrawerState((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleResponseChange = (response: any) => {
    if (firstMount.current) {
      setTransfersCount(response?.data?.data?.results?.length ?? 0);
      firstMount.current = false;
    }
  };

  const handleDownloadAch = (details: any) => {
    if (!details?.ach_document_url) return;
    downloadAchRowId.current = details?.transfer_id;
    const eventName =
      BALANCE_TRANSFERS_EVENTS.TRANSFERS_PAGE.TRANSFER_PROOF_DOWNLOADED;

    amplitude.track(eventName, {
      ...getCommonEventProperties(details),
    });

    handleStoreLastAction(eventName);

    downloadAch.mutate(details.ach_document_url);
  };

  const handlers: Record<string, IButton> = {
    openDetails: {
      onClickHandler: handleOpenDrawer,
    },
    downloadAch: {
      onClickHandler: handleDownloadAch,
      disabled: downloadAch.isPending,
      loading: downloadAch.isPending,
      id: downloadAchRowId.current,
    },
  };

  const columns = useMemo(
    () => COLUMNS_MAP({ t, lang, handlers }),
    [t, lang, handlers]
  );

  return {
    accountId,
    haveAccounts,
    shouldDisableQuery,
    transfersQueryKey,
    columns,
    drawerState,
    handleCloseDrawer,
    handleTableRowClick,
    handleResponseChange,
    transfersCount,
    firstMount,
  };
};
